'use client';

import React, { FC } from 'react';
import { useRouter } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import { DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import {
  RegularPerson,
  RegularInfo,
  RegularOpen,
} from '../../../../../common/svgIcons';
import { getUrlToRedirectAfterLogoutAction } from '@bts-web/core-features/auth';
import { accountLevelNameByApp, getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../../../statsig/sdk/provider';
import {
  TopBarMenuItem,
  TopBarMenuItemProps,
} from '../TopBarMenuItem/TopBarMenuItem';
import { UserTopBarMenuTranslations } from '../UserTopBarMenu/UserTopBarMenu';
import { useClientUserInfo } from '../../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { IslamicLabel } from '../../../../../common/components/IslamicBanking/IslamicLabel';
import { DocumentationExternalLink } from '../../../../../common/components/DocumentationExternalLink/DocumentationExternalLink';
import { ButtonBase } from '../../../../../common/components/ButtonBase/ButtonBase';

interface ITopBarMenuProps {
  TopBarMenuItems: TopBarMenuItemProps[];
  translatedValues: Pick<
    UserTopBarMenuTranslations,
    | 'generalLogout'
    | 'accountLevelUpgradeTitle'
    | 'accountLevelUpgradeSubtitle'
    | 'yourAccount'
    | 'islamicBankingAccount'
    | 'islamicBankingAccountDescription'
    | 'islamicBankingAccountLearnMore'
  >;
  isWithinDropdown?: boolean;
}

const userPillStyles = css({
  fontSize: 'caption.medium_medium',
  fontWeight: 'caption.medium_medium',
  letterSpacing: 'caption.medium_medium',
  lineHeight: 'caption.medium_medium',
  paddingX: 'extra_small_2',
  paddingY: 'extra_small_3',
  borderRadius: '32px',
  width: 'fit-content',
});

export const TopBarMenu: FC<ITopBarMenuProps> = ({
  TopBarMenuItems,
  isWithinDropdown,
  translatedValues,
}) => {
  const { appName, disableLogoutButton, islamicComplianceUrl } = getAppConfig();

  const feature_accountLevel = useFeatureFlag('feature_accountLevel');

  const userAccountLevels = accountLevelNameByApp?.[appName];

  const { accountLevel, isIslamicUser } = useClientUserInfo() ?? {};

  const { t } = useClientTranslation();

  const router = useRouter();

  const isPremiumUser = accountLevel?.name === userAccountLevels?.premium.name;

  const ItemWrapperElement = isWithinDropdown
    ? DropdownMenuItem
    : React.Fragment;

  const onClickLogout = async () => {
    const redirectUrl = await getUrlToRedirectAfterLogoutAction();

    router.push(redirectUrl as string);
  };

  const isAccountDetailsSectionVisible = feature_accountLevel || isIslamicUser;

  return (
    <div
      className={css({
        ...(isWithinDropdown
          ? {
              borderRadius: '24px',
              overflow: 'auto',
            }
          : {}),
        display: 'flex',
        flexDirection: 'column',
        gap: 'medium',
        backgroundColor: 'neutrals.fill_quinary',
      })}
    >
      {isAccountDetailsSectionVisible && (
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: 'small',
            paddingX: 'medium',
            paddingTop: 'medium',
          })}
        >
          <div
            className={css({
              backgroundColor: 'neutrals.fill_quaternary',
              color: 'neutrals.fill_secondary',
              borderRadius: '100%',
              p: 'extra_small_2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              border: '4px solid transparent',
              ...(isPremiumUser && {
                background:
                  'linear-gradient(#EAEAEA, #EAEAEA) padding-box, linear-gradient(to bottom, rgba(166, 140, 110, 1), rgba(244, 222, 184, 1)) border-box',
              }),
              ...(isIslamicUser && {
                backgroundColor: 'brand.fill_primary',
                '& > span': {
                  color: 'neutrals.text_primary_inverted',
                },
              }),
            })}
          >
            <RegularPerson size={35} />
          </div>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: 'extra_small_2',
            })}
          >
            <span
              className={css({
                fontSize: 'headline.small_medium',
                fontWeight: 'headline.small_medium',
                letterSpacing: 'headline.small_medium',
                lineHeight: 'headline.small_medium',
              })}
            >
              {translatedValues.yourAccount}
            </span>
            {/* Uncomment this section when KYC data is available from API */}
            {/* <span
            className={css({
              fontSize: 'headline.small_medium',
              fontWeight: 'headline.small_medium',
              letterSpacing: 'headline.small_medium',
              lineHeight: 'headline.small_medium',
            })}
          >
            Pedro Valente
          </span>
          <span
            className={css({
              color: 'neutrals.text_secondary',
              fontSize: 'body.medium',
              fontWeight: 'body.medium',
              letterSpacing: 'body.medium',
              lineHeight: 'body.medium',
            })}
          >
            pedro.valente@gmail.com
          </span> */}

            <div
              className={css({
                display: 'flex',
                gap: 'extra_small_3',
              })}
            >
              {feature_accountLevel && (
                <span
                  className={`${userPillStyles} ${css({
                    backgroundColor: 'neutrals.fill_primary',
                    color: 'neutrals.text_primary_inverted',
                  })}`}
                >
                  {isPremiumUser
                    ? t(
                        userAccountLevels?.premium
                          .translationKey as keyof typeof userAccountLevels,
                      )
                    : t(
                        userAccountLevels?.standard
                          .translationKey as keyof typeof userAccountLevels,
                      )}
                </span>
              )}

              {isIslamicUser && (
                <IslamicLabel text={translatedValues.islamicBankingAccount} />
              )}
            </div>
          </div>
        </div>
      )}

      {/** DISABLE THIS UNTIL WE HAVE TENANT SUPPORT FOR UPGRADING USER TO PREMIUM */}
      {/* {feature_accountLevel && !isPremiumUser && (
        <div
          className={css({
            alignItems: 'center',
            backgroundColor: 'neutrals.card_fill_primary',
            border: '1px solid',
            borderColor: 'neutrals.stroke_secondary',
            borderRadius: '16px',
            display: 'flex',
            gap: 'extra_small',
            marginX: 'medium',
            padding: 'extra_small',
          })}
        >
          <RegularArrowStepOut size={20} />
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: 'extra_small_3',
            })}
          >
            <span
              className={css({
                color: 'neutrals.text_secondary',
                fontSize: 'body.text_medium',
                fontWeight: 'body.text_medium',
                letterSpacing: 'body.text_medium',
                lineHeight: 'body.text_medium',
              })}
            >
              {translatedValues.accountLevelUpgradeSubtitle}
            </span>
            <span
              className={css({
                color: 'neutrals.text_primary',
                fontSize: 'label.medium_bold',
                fontWeight: 'label.medium_bold',
                letterSpacing: 'label.medium_bold',
                lineHeight: 'label.medium_bold',
              })}
            >
              {translatedValues.accountLevelUpgradeTitle}
            </span>
          </div>
          <RegularChevronRight
            size={12}
            className={css({
              backgroundColor: 'neutrals.fill_quinary',
              borderRadius: '32px',
              marginInlineStart: 'auto',
              padding: 'extra_small_3',
            })}
          />
        </div>
      )} */}

      {isIslamicUser && (
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
            paddingX: 'medium',
          })}
        >
          <div
            className={css({
              display: 'flex',
              gap: 'extra_small_2',
              color: 'neutrals.text_secondary',
              alignItems: 'baseline',
              fontSize: 'body.medium',
              fontWeight: 'body.medium',
              letterSpacing: 'body.medium',
              lineHeight: 'body.medium',
            })}
          >
            <RegularInfo
              className={css({
                color: 'neutrals.text_secondary',
                display: 'inline',
                '& > svg': {
                  display: 'inline',
                },
              })}
              size={12}
            />
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: 'extra_small_2',
              })}
            >
              <p>{translatedValues.islamicBankingAccountDescription}</p>
              <DocumentationExternalLink
                href={islamicComplianceUrl || '/'}
                size="small"
                useRawOpenUrl
                className={css({
                  justifyContent: 'flex-start',
                })}
              >
                <span
                  className={css({
                    display: 'inline-flex',
                    gap: 'extra_small_4',
                    color: 'brand.text_primary',
                    fontSize: 'body.medium_medium',
                    fontWeight: 'body.medium_medium',
                    letterSpacing: 'body.medium_medium',
                    lineHeight: 'body.medium_medium',
                    whiteSpace: 'nowrap',
                  })}
                >
                  {translatedValues.islamicBankingAccountLearnMore}{' '}
                  <RegularOpen
                    className={css({
                      verticalAlign: 'middle',
                      '& > svg': {
                        stroke: 'brand.text_primary',
                      },
                    })}
                    color="brand.text_primary"
                    size={13}
                  />
                </span>
              </DocumentationExternalLink>
            </div>
          </div>
        </div>
      )}

      <div
        className={css({
          backgroundColor: 'neutrals.card_fill_primary',
          display: 'flex',
          flexDirection: 'column',
          gap: 'medium',
          paddingBottom: !disableLogoutButton ? 'medium' : 'none',
        })}
      >
        <ul
          aria-label="top menu"
          className={css({
            backgroundColor: 'neutrals.fill_quinary',
            display: 'flex',
            flexDirection: 'column',
          })}
        >
          {TopBarMenuItems.map((item) => (
            <li
              key={`${item.icon}-${item.label}`}
              className={css({
                borderBottom: '1px solid',
                borderBottomColor: 'neutrals.divider',
                width: '100%',
                '&:first-of-type a': {
                  borderStartStartRadius: '24px',
                  borderStartEndRadius: '24px',
                },
                '&:last-of-type': {
                  borderBottom: 'none',
                },
              })}
            >
              <ItemWrapperElement>
                <TopBarMenuItem {...item} />
              </ItemWrapperElement>
            </li>
          ))}
        </ul>

        {!disableLogoutButton && (
          <div
            className={css({
              display: 'flex',
              justifyContent: 'center',
              paddingX: 'medium',
            })}
          >
            <ButtonBase size="large" onClick={onClickLogout}>
              {translatedValues.generalLogout}
            </ButtonBase>
          </div>
        )}
      </div>
    </div>
  );
};
